import { useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import type { ReactNode } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useFullScreen } from '../../hooks/useFullScreen';
import { useSwipeGestures } from '../../hooks/useSwipeGestures';

interface PanelContainerProps {
  children: [ReactNode, ReactNode]; // [Editor, Preview]
  className?: string;
}

const PanelContainer = ({ children, className = '' }: PanelContainerProps) => {
  const [activeTab, setActiveTab] = useState<'editor' | 'preview'>('editor');
  const [isResizing, setIsResizing] = useState(false);
  const { state, dispatch } = useAppContext();
  const { isFullScreen, isEditorFocused, isPreviewFocused, isSplitMode } = useFullScreen();

  // Swipe gestures for mobile navigation with enhanced feedback
  const swipeRef = useSwipeGestures({
    onSwipeLeft: () => {
      if (activeTab === 'editor') {
        setActiveTab('preview');
      }
    },
    onSwipeRight: () => {
      if (activeTab === 'preview') {
        setActiveTab('editor');
      }
    },
    threshold: 60, // Slightly higher threshold for more deliberate swipes
    velocity: 0.4  // Moderate velocity requirement
  });

  // Enhanced tab icons
  const getTabIcon = (tab: 'editor' | 'preview', isActive: boolean) => {
    const iconClass = `w-4 h-4 ${isActive ? 'text-current' : 'text-gray-400 dark:text-gray-500'}`;
    
    if (tab === 'editor') {
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      );
    } else {
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      );
    }
  };

  return (
    <>
      {/* Mobile Layout - Enhanced Tabbed Interface (< 768px) */}
      <div className="md:hidden flex-1 flex flex-col cs-animate-fade-in" ref={swipeRef}>
        {/* Enhanced Mobile Tab Navigation */}
        <div className="flex bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 relative">
          {/* Active tab indicator */}
          <div 
            className={`absolute bottom-0 h-0.5 bg-blue-500 transition-transform duration-300 ease-out ${
              activeTab === 'editor' ? 'w-1/2 translate-x-0' : 'w-1/2 translate-x-full'
            }`}
          />
          
          {/* Swipe hint */}
          <div className="absolute top-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-gray-300 dark:bg-gray-600 rounded-full opacity-40"></div>
          
          <button
            onClick={() => setActiveTab('editor')}
            className={`flex-1 px-4 py-4 text-sm font-medium transition-all duration-200 touch-manipulation relative ${
              activeTab === 'editor'
                ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 active:bg-gray-100 dark:active:bg-gray-700'
            }`}
          >
            <span className="flex items-center justify-center gap-3">
              {getTabIcon('editor', activeTab === 'editor')}
              <span className="font-medium">Editor</span>
            </span>
          </button>
          
          <button
            onClick={() => setActiveTab('preview')}
            className={`flex-1 px-4 py-4 text-sm font-medium transition-all duration-200 touch-manipulation relative ${
              activeTab === 'preview'
                ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 active:bg-gray-100 dark:active:bg-gray-700'
            }`}
          >
            <span className="flex items-center justify-center gap-3">
              {getTabIcon('preview', activeTab === 'preview')}
              <span className="font-medium">Preview</span>
            </span>
          </button>
        </div>

        {/* Enhanced Mobile Panel Content */}
        <div className="flex-1 relative overflow-hidden">
          {/* Editor Panel */}
          <div
            className={`absolute inset-0 transition-all duration-300 ease-out ${
              activeTab === 'editor' 
                ? 'opacity-100 z-10 translate-x-0' 
                : 'opacity-0 z-0 translate-x-[-20px] pointer-events-none'
            }`}
          >
            {children[0]}
          </div>
          
          {/* Preview Panel */}
          <div
            className={`absolute inset-0 transition-all duration-300 ease-out ${
              activeTab === 'preview' 
                ? 'opacity-100 z-10 translate-x-0' 
                : 'opacity-0 z-0 translate-x-[20px] pointer-events-none'
            }`}
          >
            {children[1]}
          </div>
        </div>
      </div>

      {/* Desktop Layout - Enhanced Resizable Panels (>= 768px) */}
      <div className={`hidden md:flex flex-1 cs-animate-fade-in ${className}`}>
        <PanelGroup 
          direction="horizontal"
          onLayout={(sizes) => {
            // Update panel sizes - persistence handled automatically by AppContext
            if (sizes.length === 2 && sizes[0] > 0 && sizes[1] > 0) {
              const leftWidth = Math.round(sizes[0]);
              dispatch({ type: 'SET_PANEL_SPLIT', payload: leftWidth });
            }
          }}
        >
          <Panel 
            defaultSize={isFullScreen && isEditorFocused && !isSplitMode ? 100 : state.preferences.panelSplit}
            minSize={isFullScreen && isEditorFocused && !isSplitMode ? 100 : 25}
            maxSize={isFullScreen && isEditorFocused && !isSplitMode ? 100 : 75}
            className={`editor-panel ${isFullScreen && isPreviewFocused && !isSplitMode ? 'hidden' : ''}`}
          >
            <div className="h-full">
              {children[0]}
            </div>
          </Panel>
          
          {/* Enhanced Resize Handle */}
          {(!isFullScreen || isSplitMode) && (
            <PanelResizeHandle 
              className="group relative"
              onDragging={(isDragging) => setIsResizing(isDragging)}
            >
              <div className={`w-1 h-full bg-gray-200 dark:bg-gray-700 group-hover:bg-blue-500 dark:group-hover:bg-blue-500 transition-all duration-200 ${
                isResizing ? 'bg-blue-500 shadow-lg' : ''
              }`}>
                {/* Resize indicator */}
                <div className="absolute inset-y-0 -left-1 -right-1 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="w-4 h-12 bg-blue-500 dark:bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 8 8">
                      <circle cx="2" cy="2" r="0.5"/>
                      <circle cx="2" cy="4" r="0.5"/>
                      <circle cx="2" cy="6" r="0.5"/>
                      <circle cx="6" cy="2" r="0.5"/>
                      <circle cx="6" cy="4" r="0.5"/>
                      <circle cx="6" cy="6" r="0.5"/>
                    </svg>
                  </div>
                </div>
              </div>
            </PanelResizeHandle>
          )}
          
          <Panel 
            defaultSize={isFullScreen && isPreviewFocused && !isSplitMode ? 100 : (100 - state.preferences.panelSplit)}
            minSize={isFullScreen && isPreviewFocused && !isSplitMode ? 100 : 25}
            maxSize={isFullScreen && isPreviewFocused && !isSplitMode ? 100 : 75}
            className={`preview-panel ${isFullScreen && isEditorFocused && !isSplitMode ? 'hidden' : ''}`}
          >
            <div className="h-full">
              {children[1]}
            </div>
          </Panel>
        </PanelGroup>
      </div>

      {/* Resize feedback overlay */}
      {isResizing && (
        <div className="fixed inset-0 bg-blue-500/5 pointer-events-none z-50 transition-opacity duration-200" />
      )}
    </>
  );
};

export default PanelContainer;